import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar, Avatar, Box, Button, Container, IconButton, Menu, MenuItem, Tab, Tabs, Toolbar, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { DOCUMENT, documentChromeTabsSx } from '../constants/documentTheme';
import { MEMORIES_BRAND, memoriesWordmarkSx } from '../constants/memoriesBrand';
import { memoriesAuthEntryUrls, performMemoriesCrossLogout } from '../utils/memoriesAuthBridge';
import { isUsableImageUrl } from '../utils/itineraryItemImage';

const MemoriesBrandMark = () => {
  const { t } = useTranslation();
  return (
    <Box
      component={RouterLink}
      to="/"
      aria-label={t('memories.brand')}
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        textDecoration: 'none',
        color: DOCUMENT.ivory,
        py: 0.25,
        transition: 'opacity 0.2s ease',
        '&:hover': { opacity: 0.88 },
      }}
    >
      <Box
        component="img"
        src={MEMORIES_BRAND.markLight}
        alt=""
        sx={{
          height: { xs: 28, md: 34 },
          width: 'auto',
          display: 'block',
          objectFit: 'contain',
          filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.2))',
        }}
      />
      <Typography
        component="span"
        sx={{
          ...memoriesWordmarkSx,
          mt: 0.65,
          fontSize: { xs: 24, md: 30 },
          color: DOCUMENT.ivory,
        }}
      >
        {t('memories.title')}
      </Typography>
      <Typography
        component="span"
        sx={{
          mt: 0.55,
          fontFamily: DOCUMENT.sans,
          fontWeight: 400,
          fontSize: { xs: 8.5, md: 9.5 },
          letterSpacing: '0.28em',
          lineHeight: 1,
          textTransform: 'uppercase',
          color: 'rgba(201,169,97,0.92)',
        }}
      >
        {t('memories.brandByline')}
      </Typography>
    </Box>
  );
};

/** Chrome for memories.dejavunomad.com — Memories brand lockup only. */
const MemoriesNavbar = () => {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const displayName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim()
    || user?.username
    || t('nav.userFallback');
  const avatarLetter = (displayName || user?.email || 'U').charAt(0).toUpperCase();

  const handleLogout = () => {
    setAnchorEl(null);
    // Hard navigation only — do not call logout() first (that races PrivateRoute
    // into re-bridging the main-app session and logging the user back in).
    performMemoriesCrossLogout();
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      className="professional-navbar"
      sx={{
        backgroundColor: DOCUMENT.nav,
        backgroundImage: 'none',
        backdropFilter: 'none',
        borderBottom: 'none',
        boxShadow: 'none',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{ justifyContent: 'space-between', py: 1.25, minHeight: { xs: 72, md: 88 } }}
        >
          <MemoriesBrandMark />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {!loading && (
              user ? (
                <>
                  <IconButton onClick={(event) => setAnchorEl(event.currentTarget)} sx={{ p: 0 }}>
                    <Avatar
                      src={isUsableImageUrl(user?.avatar_url) ? user.avatar_url : (isUsableImageUrl(user?.avatar) ? user.avatar : undefined)}
                      sx={{
                        width: 40,
                        height: 40,
                        fontWeight: 600,
                        backgroundColor: '#591716',
                        color: 'white',
                        fontSize: '1.1rem',
                      }}
                    >
                      {avatarLetter}
                    </Avatar>
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    slotProps={{
                      paper: {
                        sx: { minWidth: 220, borderRadius: 1 },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: `1px solid ${DOCUMENT.rule}`,
                        maxWidth: 280,
                      }}
                    >
                      <Typography
                        sx={{
                          color: DOCUMENT.ink,
                          fontWeight: 600,
                          fontSize: 14,
                          lineHeight: 1.3,
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {displayName}
                      </Typography>
                      {user.email && (
                        <Typography
                          sx={{
                            color: DOCUMENT.ink3,
                            fontSize: 12.5,
                            mt: 0.35,
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {user.email}
                        </Typography>
                      )}
                    </Box>
                    <MenuItem onClick={handleLogout}>{t('nav.logout')}</MenuItem>
                  </Menu>
                </>
              ) : (
                <Button
                  component="a"
                  href={memoriesAuthEntryUrls(
                    typeof window !== 'undefined' ? window.location.href : '/'
                  ).login}
                  sx={{
                    color: DOCUMENT.ivory,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    fontSize: 11,
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.75,
                  }}
                >
                  {t('nav.signIn')}
                </Button>
              )
            )}
          </Box>
        </Toolbar>
      </Container>

      {!loading && (
        <Box
          sx={{
            backgroundColor: DOCUMENT.nav,
            borderTop: `1px solid ${DOCUMENT.navRule}`,
            borderBottom: `1px solid ${DOCUMENT.navRule}`,
          }}
        >
          <Container maxWidth="xl">
            <Tabs value={0} sx={documentChromeTabsSx}>
              <Tab
                label={t('nav.memories')}
                component={RouterLink}
                to="/"
              />
            </Tabs>
          </Container>
        </Box>
      )}
    </AppBar>
  );
};

export default MemoriesNavbar;
